import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  GovUKPageContainer,
  GovUKBackLink,
  GovUKTypography,
  GovUKButton,
  GovUKButtonGroup,
  GovUKInput,
  GovUKNotificationBanner,
} from '@/components/govuk'
import { GOVUK_COLOURS } from '@/components/govuk/colours'
import { Eye, EyeOff, ImagePlus, X } from 'lucide-react'
import { NoteEditor } from '@/components/NoteEditor'
import type { NoteEditorHandle } from '@/components/NoteEditor'
import { uploadMediaFile } from '@/data/media/uploadMediaClient'
import { uploadNoteMediaThumb } from '@/data/media/uploadNoteMediaThumb'
import { getMediaUrls } from '@/data/media/getMediaUrls'
import type { UploadedMedia } from '@/data/media/mediaSchemas'
import { extractMediaKeys, mediaKindFromKey, urlKeysFor } from '@/lib/noteMedia'
import { getNoteById } from '@/data/notes/getNoteById'
import { getUsers } from '@/data/users/getUsers'
import { createNote } from '@/data/notes/createNote'
import { updateNote } from '@/data/notes/updateNote'
import { deleteNote } from '@/data/notes/deleteNote'
import { toggleNoteHidden } from '@/data/notes/toggleNoteHidden'
import { segmentsToText, applyEdit } from '@/lib/noteSegments'
import { getUserColour } from '@/lib/userColour'
import { queryKeys } from '@/lib/queryKeys'
import { useCurrentUser } from './CurrentUserContext'
import type { Segment } from '@/lib/noteSegments'
import 'govuk-frontend/dist/govuk/govuk-frontend.min.css'

interface NoteEditorPageProps {
  noteId?: string
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function NoteEditorPage({ noteId }: NoteEditorPageProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { currentUser } = useCurrentUser()
  const isNew = !noteId

  // ── Remote data ───────────────────────────────────────────────────────────
  const {
    data: note,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.notes.detail(noteId ?? ''),
    queryFn: () => getNoteById({ data: { noteId: noteId! } }),
    enabled: !!noteId,
  })

  const { data: users = [] } = useQuery({
    queryKey: queryKeys.users.list(),
    queryFn: () => getUsers(),
  })

  // ── Local state ───────────────────────────────────────────────────────────
  const [title, setTitle] = useState('')
  const [editText, setEditText] = useState('')
  const [segments, setSegments] = useState<Segment[]>([])
  const [lastSaved, setLastSaved] = useState<string | null>(null)
  const [lastEditor, setLastEditor] = useState('')
  const [savedFlash, setSavedFlash] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [isHidden, setIsHidden] = useState(false)
  const isHarry = currentUser === 'Harry'

  // ── Inline media ──────────────────────────────────────────────────────────
  const editorRef = useRef<NoteEditorHandle>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadedUrls, setUploadedUrls] = useState<Record<string, string>>({})
  const [uploadingCount, setUploadingCount] = useState(0)
  const [uploadPercent, setUploadPercent] = useState<number | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [modalMediaKey, setModalMediaKey] = useState<string | null>(null)

  const mediaKeys = useMemo(
    () => urlKeysFor([...new Set(extractMediaKeys(editText))].sort()),
    [editText],
  )

  const { data: fetchedUrls = {} } = useQuery({
    queryKey: queryKeys.media.urls(mediaKeys),
    queryFn: () => getMediaUrls({ data: { keys: mediaKeys } }),
    enabled: mediaKeys.length > 0,
    staleTime: 1000 * 60 * 60, // presigned URLs last 6h; refresh well before then
    refetchOnWindowFocus: false,
  })

  const mediaUrls = useMemo(
    () => ({ ...fetchedUrls, ...uploadedUrls }),
    [fetchedUrls, uploadedUrls],
  )

  /** Grab a poster frame from a video file as base64 JPEG (null if the browser can't decode it) */
  const captureVideoThumb = (file: File): Promise<string | null> =>
    new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(file)
      const video = document.createElement('video')
      let settled = false
      const done = (result: string | null) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        URL.revokeObjectURL(objectUrl)
        video.removeAttribute('src')
        resolve(result)
      }
      const timer = setTimeout(() => done(null), 10000)
      video.muted = true
      video.playsInline = true
      video.preload = 'auto'
      video.onerror = () => done(null)
      video.onloadedmetadata = () => {
        video.currentTime = Math.min(0.5, (video.duration || 1) / 2)
      }
      video.onseeked = () => {
        try {
          const scale = Math.min(1, 640 / (video.videoWidth || 640))
          const canvas = document.createElement('canvas')
          canvas.width = Math.max(1, Math.round(video.videoWidth * scale))
          canvas.height = Math.max(1, Math.round(video.videoHeight * scale))
          const ctx = canvas.getContext('2d')
          if (!ctx) return done(null)
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
          done(dataUrl.slice(dataUrl.indexOf(',') + 1))
        } catch {
          done(null)
        }
      }
      video.src = objectUrl
    })

  async function handleFilesSelected(fileList: FileList | File[] | null) {
    if (!fileList || fileList.length === 0) return
    const files = Array.from(fileList)
    setUploadError(null)

    const wrongType = files.find(
      (f) => !f.type.startsWith('image/') && !f.type.startsWith('video/'),
    )
    if (wrongType) {
      setUploadError(`"${wrongType.name}" is not a photo or video.`)
      return
    }

    setUploadingCount(files.length)
    try {
      const uploaded: (UploadedMedia & {
        thumbKey?: string
        thumbUrl?: string
      })[] = []
      for (const file of files) {
        const result = await uploadMediaFile(file, (fraction) =>
          setUploadPercent(Math.round(fraction * 100)),
        )
        setUploadPercent(null)
        let thumb: { key: string; url: string } | null = null
        if (result.kind === 'video') {
          const thumbBase64 = await captureVideoThumb(file)
          if (thumbBase64) {
            try {
              thumb = await uploadNoteMediaThumb({
                data: { forKey: result.key, dataBase64: thumbBase64 },
              })
            } catch {
              thumb = null // thumbnail is best-effort; the chip still works
            }
          }
        }
        uploaded.push({
          ...result,
          thumbKey: thumb?.key,
          thumbUrl: thumb?.url,
        })
        setUploadingCount((n) => n - 1)
      }
      setUploadedUrls((prev) => ({
        ...prev,
        ...Object.fromEntries(
          uploaded.flatMap((u) => [
            [u.key, u.url] as const,
            ...(u.thumbKey && u.thumbUrl
              ? [[u.thumbKey, u.thumbUrl] as const]
              : []),
          ]),
        ),
      }))
      editorRef.current?.insertMedia(uploaded)
    } catch (err) {
      setUploadError(
        err instanceof Error
          ? err.message
          : 'Upload failed — please try again.',
      )
    } finally {
      setUploadingCount(0)
      setUploadPercent(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // Sync from server when note loads or noteId changes
  useEffect(() => {
    if (note) {
      setTitle(note.title)
      setSegments(note.segments ?? [])
      setEditText(segmentsToText(note.segments ?? []))
      setLastSaved(note.updatedAt)
      setLastEditor(note.lastEditedBy ?? '')
      setIsHidden(note.hidden ?? false)
      setConfirmDelete(false)
    }
  }, [note?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset when navigating to a new note
  useEffect(() => {
    if (!noteId) {
      setTitle('')
      setEditText('')
      setSegments([])
      setLastSaved(null)
      setLastEditor('')
      setConfirmDelete(false)
    }
  }, [noteId])

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (data: {
      title: string
      segments: Segment[]
      lastEditedBy: string
    }) => createNote({ data }),
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.all })
      navigate({ to: '/notes/$noteId', params: { noteId: id } })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: {
      noteId: string
      title: string
      segments: Segment[]
      lastEditedBy: string
    }) => updateNote({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.all })
      setLastSaved(new Date().toISOString())
      setLastEditor(currentUser ?? '')
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 2500)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteNote({ data: { noteId: id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.all })
      navigate({ to: '/notes/new' })
    },
  })

  const hiddenMutation = useMutation({
    mutationFn: (id: string) => toggleNoteHidden({ data: { noteId: id } }),
    onSuccess: (result) => {
      setIsHidden(result.hidden)
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.all })
    },
  })

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    const editor = currentUser ?? ''
    const newSegments = applyEdit(segments, editText, editor)
    setSegments(newSegments) // optimistic update so attribution view refreshes immediately

    if (isNew) {
      createMutation.mutate({
        title: title.trim() || 'Untitled',
        segments: newSegments,
        lastEditedBy: editor,
      })
    } else if (noteId) {
      updateMutation.mutate({
        noteId,
        title: title.trim() || 'Untitled',
        segments: newSegments,
        lastEditedBy: editor,
      })
    }
  }, [
    segments,
    editText,
    currentUser,
    isNew,
    noteId,
    title,
    createMutation,
    updateMutation,
  ])

  // Ctrl+S / Cmd+S
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        handleSave()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleSave])

  const isSaving = createMutation.isPending || updateMutation.isPending
  const isDeleting = deleteMutation.isPending

  // ── Loading / error states ────────────────────────────────────────────────
  if (!isNew && isLoading) {
    return (
      <GovUKPageContainer>
        {isMobile && <GovUKBackLink to="/notes">Notes</GovUKBackLink>}
        <GovUKTypography
          variant="body"
          style={{ color: GOVUK_COLOURS.darkGrey }}
        >
          Loading…
        </GovUKTypography>
      </GovUKPageContainer>
    )
  }

  if (!isNew && isError) {
    return (
      <GovUKPageContainer>
        {isMobile && <GovUKBackLink to="/notes">Notes</GovUKBackLink>}
        <GovUKNotificationBanner
          type="error"
          message="This note could not be loaded."
        />
        <GovUKButton onClick={() => navigate({ to: '/notes/new' })}>
          New note
        </GovUKButton>
      </GovUKPageContainer>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <GovUKPageContainer>
      {isMobile && <GovUKBackLink to="/notes">Notes</GovUKBackLink>}

      {/* Title */}
      <GovUKInput
        id="note-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Note title…"
        style={{
          width: '100%',
          maxWidth: '100%',
          fontSize: '24px',
          fontWeight: 700,
          border: 'none',
          borderBottom: `2px solid ${GOVUK_COLOURS.midGrey}`,
          borderRadius: 0,
          padding: '8px 0',
          marginBottom: '24px',
          outline: 'none',
          backgroundColor: 'transparent',
        }}
      />

      {/* Editor — coloured by author, always editable */}
      <NoteEditor
        ref={editorRef}
        segments={segments}
        users={users}
        currentUser={currentUser ?? ''}
        mediaUrls={mediaUrls}
        onChange={setEditText}
        onExpandMedia={setModalMediaKey}
        onMediaFiles={handleFilesSelected}
      />

      {uploadError && (
        <div style={{ marginTop: '12px' }}>
          <GovUKNotificationBanner type="error" message={uploadError} />
        </div>
      )}

      {/* Action bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginTop: '12px',
          flexWrap: 'wrap',
        }}
      >
        <GovUKButton
          onClick={handleSave}
          disabled={isSaving}
          style={{ marginBottom: 0 }}
        >
          {isSaving ? 'Saving…' : isNew ? 'Save note' : 'Save changes'}
        </GovUKButton>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFilesSelected(e.target.files)}
        />
        <GovUKButton
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingCount > 0}
          style={{
            marginBottom: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <ImagePlus size={18} />
          {uploadingCount > 0
            ? `Uploading${uploadingCount > 1 ? ` (${uploadingCount} left)` : ''}${
                uploadPercent !== null ? ` ${uploadPercent}%` : ''
              }…`
            : 'Add photos / videos'}
        </GovUKButton>

        {savedFlash && (
          <span
            style={{
              color: GOVUK_COLOURS.green,
              fontWeight: 700,
              fontSize: '16px',
            }}
          >
            ✓ Saved
          </span>
        )}

        <GovUKTypography
          variant="body-s"
          style={{ color: GOVUK_COLOURS.darkGrey, margin: 0 }}
        >
          <kbd>Ctrl+S</kbd> / <kbd>⌘S</kbd> to save
        </GovUKTypography>
      </div>

      {/* Delete */}
      {!isNew && (
        <div
          style={{
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: `1px solid ${GOVUK_COLOURS.midGrey}`,
          }}
        >
          {!confirmDelete ? (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: GOVUK_COLOURS.red,
                fontSize: '15px',
                textDecoration: 'underline',
                padding: 0,
                fontFamily: 'inherit',
              }}
            >
              Delete note
            </button>
          ) : (
            <div>
              <GovUKTypography variant="body" marginBottom={3}>
                <strong>This will permanently delete the note.</strong> Are you
                sure?
              </GovUKTypography>
              <GovUKButtonGroup>
                <GovUKButton
                  variant="warning"
                  style={{ marginBottom: 0 }}
                  onClick={() => deleteMutation.mutate(noteId!)}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting…' : 'Yes, delete'}
                </GovUKButton>
                <GovUKButton
                  variant="secondary"
                  style={{ marginBottom: 0 }}
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancel
                </GovUKButton>
              </GovUKButtonGroup>
            </div>
          )}
        </div>
      )}

      {/* Footer: last saved info + visibility toggle */}
      {lastSaved && !isNew && (
        <div
          style={{
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <GovUKTypography
            variant="body-s"
            style={{ color: GOVUK_COLOURS.darkGrey, margin: 0 }}
          >
            {formatDate(lastSaved)}
          </GovUKTypography>
          {isHarry && noteId && (
            <button
              type="button"
              title={
                isHidden
                  ? 'Note is hidden — click to show'
                  : 'Note is visible — click to hide'
              }
              onClick={() => hiddenMutation.mutate(noteId)}
              disabled={hiddenMutation.isPending}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px',
                color: GOVUK_COLOURS.darkGrey,
                opacity: isHidden ? 0.5 : 0.3,
                lineHeight: 1,
              }}
            >
              {isHidden ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          )}
        </div>
      )}

      {/* Full-size media viewer */}
      <Dialog
        open={!!modalMediaKey}
        onClose={() => setModalMediaKey(null)}
        maxWidth="lg"
        slotProps={{ paper: { style: { backgroundColor: '#0b0c0c' } } }}
      >
        <IconButton
          onClick={() => setModalMediaKey(null)}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#ffffff',
            zIndex: 1,
          }}
        >
          <X size={22} />
        </IconButton>
        <DialogContent
          style={{ padding: 0, display: 'flex', backgroundColor: '#0b0c0c' }}
        >
          {modalMediaKey &&
            mediaUrls[modalMediaKey] &&
            (mediaKindFromKey(modalMediaKey) === 'video' ? (
              <video
                controls
                autoPlay
                playsInline
                src={mediaUrls[modalMediaKey]}
                style={{
                  maxWidth: '90vw',
                  maxHeight: '85vh',
                  display: 'block',
                }}
              />
            ) : (
              <img
                src={mediaUrls[modalMediaKey]}
                alt=""
                style={{
                  maxWidth: '90vw',
                  maxHeight: '85vh',
                  display: 'block',
                  objectFit: 'contain',
                }}
              />
            ))}
        </DialogContent>
      </Dialog>
    </GovUKPageContainer>
  )
}
